'use client';

import { useEffect, useRef, useState } from 'react';
import { useLoadedWebpSource } from '@/components/tools/webp-tool-shared';
import { consumeToolUsage, formatToolCreditSuccess } from '@/lib/browser-tool-usage';
import { formatFileSize, getBaseName } from '@/lib/browser-image-tools';
import { recompressStillWebp } from '@/lib/browser-webp-tools';

type MethodPreset = 'balanced' | 'max-quality' | 'smaller';

export default function BrowserWebpQualityRecompressor() {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { source, loadSource, clearSource } = useLoadedWebpSource();
  const [quality, setQuality] = useState(82);
  const [lossless, setLossless] = useState(false);
  const [methodPreset, setMethodPreset] = useState<MethodPreset>('balanced');
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [downloadName, setDownloadName] = useState<string | null>(null);
  const [resultPreviewUrl, setResultPreviewUrl] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState<number | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      [downloadUrl, resultPreviewUrl].forEach((url) => {
        if (url) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [downloadUrl, resultPreviewUrl]);

  function resetOutputState() {
    [downloadUrl, resultPreviewUrl].forEach((url) => {
      if (url) {
        URL.revokeObjectURL(url);
      }
    });

    setDownloadUrl(null);
    setDownloadName(null);
    setResultPreviewUrl(null);
    setResultSize(null);
  }

  async function handleFileSelection(file: File | null) {
    resetOutputState();
    setErrorMessage(null);
    setCreditMessage(null);

    if (!file) {
      clearSource();
      return;
    }

    try {
      await loadSource(file);
    } catch (error) {
      clearSource();
      setErrorMessage(
        error instanceof Error ? error.message : 'The WebP file could not be loaded.',
      );
    }
  }

  async function runRecompression() {
    if (!source) {
      setErrorMessage('Choose a WebP file first.');
      return;
    }

    if (source.metadata.animated) {
      setErrorMessage('This WebP is animated. Use WebP Optimizer for animated WebP files.');
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);
    setCreditMessage(null);

    try {
      const credits = await consumeToolUsage(
        'webp-quality-recompressor',
        'Credits are not available for another WebP recompression right now.',
      );
      const blob = await recompressStillWebp({
        file: source.file,
        quality,
        lossless,
        methodPreset,
      });
      const nextUrl = URL.createObjectURL(blob);

      resetOutputState();
      setDownloadUrl(nextUrl);
      setResultPreviewUrl(nextUrl);
      setDownloadName(`${getBaseName(source.file.name)}-recompressed.webp`);
      setResultSize(blob.size);
      setCreditMessage(formatToolCreditSuccess('WebP recompression', credits));
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'The WebP file could not be recompressed.',
      );
    } finally {
      setIsProcessing(false);
    }
  }

  const savings =
    source && resultSize !== null
      ? `${Math.round((1 - resultSize / Math.max(source.file.size, 1)) * 100)}%`
      : null;

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>Recompress a still WebP with quality controls</h2>
          <p>
            Tune WebP quality, switch between lossy and lossless export, and compare the resulting
            file size before you download it.
          </p>
        </div>
        <span className="tool-live-badge">WebP</span>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void handleFileSelection(event.dataTransfer.files?.[0] ?? null);
            }}
            type="button"
          >
            <div>
              <strong>{source ? source.file.name : 'Choose one WebP or drop it here'}</strong>
              <span>
                {source
                  ? `${formatFileSize(source.file.size)} • ${source.metadata.width} × ${source.metadata.height}`
                  : 'Still WebP only'}
              </span>
            </div>
          </button>
          <input
            ref={inputRef}
            accept="image/webp,.webp"
            className="tool-live-file-input"
            onChange={(event) => void handleFileSelection(event.target.files?.[0] ?? null)}
            type="file"
          />

          <div className="tool-live-controls">
            <label className="tool-live-field">
              <span>Quality</span>
              <input
                max="100"
                min="1"
                onChange={(event) => {
                  setQuality(Math.max(1, Number(event.target.value) || 82));
                  resetOutputState();
                }}
                step="1"
                type="range"
                value={quality}
              />
              <small>{quality}</small>
            </label>
            <label className="tool-live-field">
              <span>Method preset</span>
              <select
                onChange={(event) => {
                  setMethodPreset(event.target.value as MethodPreset);
                  resetOutputState();
                }}
                value={methodPreset}
              >
                <option value="balanced">Balanced</option>
                <option value="max-quality">Max quality</option>
                <option value="smaller">Smaller file</option>
              </select>
            </label>
          </div>

          <div className="tool-live-controls">
            <label className="tool-live-checkbox">
              <input
                checked={lossless}
                onChange={(event) => {
                  setLossless(event.target.checked);
                  resetOutputState();
                }}
                type="checkbox"
              />
              <span>Use lossless WebP</span>
            </label>
          </div>

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={!source || isProcessing}
              onClick={() => void runRecompression()}
              type="button"
            >
              {isProcessing ? 'Recompressing…' : 'Recompress WebP'}
            </button>
            {downloadUrl && downloadName ? (
              <a className="button button-ghost" download={downloadName} href={downloadUrl}>
                Download WebP
              </a>
            ) : null}
          </div>

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>WebP settings</h3>
          <div className="tool-live-stats">
            <div>
              <strong>Quality</strong>
              <span>{quality}</span>
            </div>
            <div>
              <strong>Mode</strong>
              <span>{lossless ? 'Lossless' : 'Lossy'}</span>
            </div>
            <div>
              <strong>Preset</strong>
              <span>{methodPreset}</span>
            </div>
            {resultSize ? (
              <div>
                <strong>Output size</strong>
                <span>{formatFileSize(resultSize)}</span>
              </div>
            ) : null}
            {savings ? (
              <div>
                <strong>Savings</strong>
                <span>{savings}</span>
              </div>
            ) : null}
          </div>
        </aside>
      </div>

      <div className="tool-live-preview">
        <h3>Preview</h3>
        <div className="tool-image-compare-grid">
          <div className="tool-image-compare-card">
            <strong>Original</strong>
            <div className="tool-live-preview-frame">
              {source ? (
                <img alt="Original WebP preview" src={source.previewUrl} />
              ) : (
                <div className="tool-editor-empty">
                  <strong>Original preview</strong>
                  <span>Upload one still WebP to inspect it here.</span>
                </div>
              )}
            </div>
          </div>
          <div className="tool-image-compare-card">
            <strong>Recompressed</strong>
            <div className="tool-live-preview-frame">
              {resultPreviewUrl ? (
                <img alt="Recompressed WebP preview" src={resultPreviewUrl} />
              ) : (
                <div className="tool-editor-empty">
                  <strong>Recompressed preview</strong>
                  <span>Run the recompressor to preview the output WebP.</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}
